import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  FlatList,
  InteractionManager,
  Keyboard,
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputKeyPressEventData,
  TextInputContentSizeChangeEventData,
  TouchableOpacity,
  View,
  ImageBackground,
} from 'react-native';
import {GiftedChat, IMessage, MessageProps} from 'react-native-gifted-chat';
import {useSelector} from 'react-redux';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {images} from '../../../../assets/icons';
import {ChatbotShape, ChatbotTechnicalWork} from '../../../../assets/images';
import {getChatHistory, postProcessPrompt} from '../../../../redux/actions/socialSupport/chatbot';
import {
  isGeneralQuestionSelector,
  isUsefulAnswerSelector,
  messagesSelector,
} from '../../../../redux/selectors/socialSupport/chatbot';
import {GradientView} from '../../../../shared/components/gradientView';
import {Text} from '../../../../shared/components/text';
import {useStyles} from '../../../../shared/hooks/styles';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {ChatbotAction, MessageFrom, PostProcessPromptRequestPayload} from '../../../../shared/models/api';
import {useTheme} from '../../../../shared/theme';
import {Message, TypingIndicator, TechnicalWorkPlaceholder} from './components';
import {useAppDispatch} from '../../../../redux';

const LINE_HEIGHT = Platform.OS === 'web' ? 20 : 22;
const MIN_INPUT_HEIGHT = Platform.OS === 'web' ? 24 : 40;
const MAX_LINES = 3;
// Для Android: MIN_INPUT_HEIGHT уже включает padding, поэтому добавляем только 2 дополнительные строки
const MAX_INPUT_HEIGHT =
  Platform.OS === 'web' ? LINE_HEIGHT * MAX_LINES : MIN_INPUT_HEIGHT + LINE_HEIGHT * (MAX_LINES - 1);
const MIN_INPUT_TOOLBAR_HEIGHT = 44;
const DESKTOP_TOOLBAR_OFFSET = 8;
const MOBILE_TOOLBAR_OFFSET = 50;
const ANDROID_KEYBOARD_GAP = 8;

interface ChatbotProps {
  header?: ReactNode;
}

export function Chatbot({header}: ChatbotProps) {
  const isDesktop = useIsDesktop();
  const {supportService} = useTheme();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const {messages, isLoading, isError} = useSelector(messagesSelector());
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [inputHeight, setInputHeight] = useState(MIN_INPUT_HEIGHT);
  const [keyboardOverlap, setKeyboardOverlap] = useState(0);
  const isGeneralQuestion = useSelector(isGeneralQuestionSelector());
  const isUsefulAnswer = useSelector(isUsefulAnswerSelector());

  const listRef = useRef<FlatList<IMessage>>(null);
  const inputRef = useRef<TextInput>(null);
  const isEmpty = useMemo(() => messages.length === 0, [messages]);
  const shouldScrollAfterSendRef = useRef(false);
  const lastMessagesLengthRef = useRef(messages.length);

  const shapeSize: {width: number; height: number; top: number} = isDesktop
    ? {width: 572, height: 876, top: 500}
    : {width: 572, height: 876, top: 600};
  const technicalWorkSize: {width: number; height: number} = isDesktop
    ? {width: 780, height: 790}
    : {width: 525, height: 774};

  const styles = useStyles(() => {
    return {
      container: {
        flex: 1,
        width: '100%',
      },
      chatContainer: {
        flex: 1,
        ...(Platform.OS === 'web' ? {paddingBottom: 8} : null),
      },
      messagesContainer: {
        justifyContent: isError ? 'center' : undefined,
      },
      customInputToolbar: {
        position: 'absolute',
        left: 10,
        right: 10,
        flexDirection: 'row',
        alignItems: 'flex-end',
        shadowColor: '#252C292E',
        shadowOpacity: 1,
        shadowOffset: {
          width: 0,
          height: 0,
        },
        shadowRadius: 18,
        elevation: 4,
        borderRadius: 14,
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: isGeneralQuestion && !isUsefulAnswer ? '#e0dcdc' : '#fff',
        minHeight: MIN_INPUT_TOOLBAR_HEIGHT,
      },
      input: {
        flex: 1,
        textAlignVertical: 'top',
        lineHeight: LINE_HEIGHT,
        minHeight: MIN_INPUT_HEIGHT,
        maxHeight: MAX_INPUT_HEIGHT,
        ...(Platform.OS === 'web' ? {outlineWidth: 0, height: inputHeight} : {height: inputHeight}),
      },
      sendButton: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: 10,
        height: MIN_INPUT_HEIGHT,
      },
    };
  });

  const sendMessage = useCallback(
    (text: string, action?: PostProcessPromptRequestPayload['action']) => {
      const trimmedText = text.trim();
      if (trimmedText.length > 0) {
        requestAnimationFrame(() => {
          setInputText('');
          setInputHeight(MIN_INPUT_HEIGHT);
        });

        shouldScrollAfterSendRef.current = true;
        dispatch(postProcessPrompt({prompt: trimmedText, action}));

        if (Platform.OS === 'web') {
          listRef.current?.scrollToOffset({offset: 0, animated: false});
        }
      }
    },
    [dispatch],
  );

  const getDiscreteHeight = useCallback((contentHeight: number) => {
    if (contentHeight <= MIN_INPUT_HEIGHT + LINE_HEIGHT / 2) {
      return MIN_INPUT_HEIGHT;
    } else if (contentHeight <= MIN_INPUT_HEIGHT + LINE_HEIGHT * 1.5) {
      return MIN_INPUT_HEIGHT + LINE_HEIGHT;
    } else {
      return MAX_INPUT_HEIGHT;
    }
  }, []);

  const handleContentSizeChange = useCallback(
    (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      const contentHeight = e.nativeEvent.contentSize.height;
      const newHeight = getDiscreteHeight(contentHeight);
      setInputHeight(newHeight);
    },
    [getDiscreteHeight],
  );

  useEffect(() => {
    if (Platform.OS === 'web' && inputRef.current) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          const element = inputRef.current as any;
          if (element && element.scrollHeight !== undefined) {
            const prevHeight = element.style.height;
            element.style.height = 'auto';
            const scrollHeight = element.scrollHeight;
            element.style.height = prevHeight;

            const newHeight = getDiscreteHeight(scrollHeight);
            setInputHeight(newHeight);
          }
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [inputText, getDiscreteHeight]);

  const onReplyPress = useCallback(
    ({id, label, action}: ChatbotAction) => {
      sendMessage(label, {id, actionType: action});
    },
    [sendMessage],
  );

  const renderMessage = useCallback(
    (props: Readonly<MessageProps<IMessage>>) => {
      if (!props.currentMessage) return null;

      const hasUserReplyAfter = props.nextMessage?.user?._id === MessageFrom.USER;

      return (
        <Message
          {...props}
          currentMessage={props.currentMessage}
          onReplyPress={onReplyPress}
          hasUserReplyAfter={hasUserReplyAfter}
        />
      );
    },
    [onReplyPress, messages],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsHistoryLoading(true);
        await dispatch(getChatHistory());
      } finally {
        setIsHistoryLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      // Since the message list is inverted, we need to change the scrolling behavior so that it works as expected.
      const node = listRef.current?.getScrollableNode();
      if (!node) return;

      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();

        node.scrollBy({
          top: -e.deltaY,
          behavior: 'auto',
        });
      };

      node.addEventListener('wheel', handleWheel, {passive: false});
      return () => node.removeEventListener('wheel', handleWheel);
    }
  }, [listRef.current]);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const prevLen = lastMessagesLengthRef.current;
    const nextLen = messages.length;
    lastMessagesLengthRef.current = nextLen;

    if (!shouldScrollAfterSendRef.current) return;
    if (nextLen <= prevLen) return;

    shouldScrollAfterSendRef.current = false;

    const scrollToBottom = () => {
      if (!listRef.current) return;

      setTimeout(() => {
        listRef.current?.scrollToOffset({offset: 0, animated: false});
      }, 100);
    };

    const task = InteractionManager.runAfterInteractions(scrollToBottom);
    return () => {
      if (task && typeof (task as any).cancel === 'function') {
        (task as any).cancel();
      }
    };
  }, [messages.length]);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    if (Platform.OS === 'android') {
      const shouldHandleAndroidKeyboard = typeof Platform.Version === 'number' ? Platform.Version >= 34 : true;

      if (!shouldHandleAndroidKeyboard) {
        setKeyboardOverlap(0);
        return;
      }

      const showSub = Keyboard.addListener('keyboardDidShow', (e) => {
        setKeyboardOverlap(Math.max(0, e.endCoordinates?.height ?? 0));
      });
      const hideSub = Keyboard.addListener('keyboardDidHide', () => {
        setKeyboardOverlap(0);
      });

      return () => {
        showSub.remove();
        hideSub.remove();
      };
    }

    // IOS
    const changeSub = Keyboard.addListener('keyboardWillChangeFrame', (e) => {
      const end = e.endCoordinates;
      if (!end) {
        setKeyboardOverlap(0);
        return;
      }
      setKeyboardOverlap(Math.max(0, end.height ?? 0));
    });
    const hideSub = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardOverlap(0);
    });
    const didHideSub = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardOverlap(0);
    });

    return () => {
      changeSub.remove();
      hideSub.remove();
      didHideSub.remove();
    };
  }, [Platform.OS]);

  const toolbarHeight = useMemo(() => {
    const verticalInputPadding = 20;
    return Math.max(MIN_INPUT_TOOLBAR_HEIGHT, inputHeight + verticalInputPadding);
  }, [inputHeight]);

  const toolbarBottom = useMemo(() => {
    if (Platform.OS === 'web') {
      return DESKTOP_TOOLBAR_OFFSET;
    }
    if (Platform.OS === 'android' && keyboardOverlap > 0) {
      return keyboardOverlap + ANDROID_KEYBOARD_GAP;
    }
    if (keyboardOverlap > 0) return keyboardOverlap;
    return Math.max(insets.bottom, MOBILE_TOOLBAR_OFFSET);
  }, [keyboardOverlap, insets.bottom]);

  const messagesBottomInset = useMemo(() => {
    return toolbarBottom + 12;
  }, [toolbarHeight, toolbarBottom]);

  return (
    <View style={styles.container}>
      <GradientView style={{height: '100%'}} gradient={{angle: 90, colors: supportService.chatbotScreen.bgGradient1}}>
        <GradientView style={{height: '100%'}} gradient={{angle: 0, colors: supportService.chatbotScreen.bgGradient2}}>
          {header}
          <View style={styles.chatContainer}>
            {isError ? (
              <ImageBackground
                source={ChatbotTechnicalWork as any}
                imageStyle={{width: technicalWorkSize.width, height: technicalWorkSize.height}}
              />
            ) : (
              <ImageBackground
                source={ChatbotShape as any}
                imageStyle={{width: shapeSize.width, height: shapeSize.height, top: shapeSize.top}}
              />
            )}

            <GiftedChat
              messages={messages}
              user={{_id: MessageFrom.USER}}
              renderMessage={renderMessage}
              renderInputToolbar={() => null}
              renderChatEmpty={() => {
                if (isHistoryLoading) return null;
                if (isError) {
                  return (
                    <TechnicalWorkPlaceholder
                      style={{transform: [{scaleY: -1}, {scaleX: Platform.OS === 'android' ? -1 : 0}]}}
                    />
                  );
                }
                return (
                  !isLoading && (
                    <Text
                      style={{
                        textAlign: 'center',
                        transform: [{scaleY: -1}, {scaleX: Platform.OS === 'android' ? -1 : 0}],
                      }}>
                      Нет сообщений
                    </Text>
                  )
                );
              }}
              keyboardShouldPersistTaps='never'
              isKeyboardInternallyHandled={false}
              renderFooter={() => <View>{isLoading && !isEmpty ? <TypingIndicator /> : null}</View>}
              messagesContainerStyle={[styles.messagesContainer, {paddingBottom: messagesBottomInset}]}
              listViewProps={{
                ref: listRef,
                contentContainerStyle: {
                  paddingTop: 63,
                  paddingHorizontal: 10,
                  justifyContent: isError ? 'center' : undefined,
                  flexGrow: isError ? 1 : undefined,
                },
                ...(Platform.OS !== 'web' && {
                  maintainVisibleContentPosition: {
                    minIndexForVisible: 0,
                  },
                }),
                scrollEventThrottle: 16,
                maxToRenderPerBatch: Platform.OS === 'web' ? 100 : 30,
                windowSize: Platform.OS === 'web' ? 41 : 31,
                initialNumToRender: Platform.OS === 'web' ? 100 : 30,
                removeClippedSubviews: Platform.OS !== 'web',
                updateCellsBatchingPeriod: Platform.OS === 'web' ? 100 : 50,
                keyExtractor: (item: IMessage) => item._id,
              }}
            />

            {!isError && (
              <View style={[styles.customInputToolbar, {bottom: toolbarBottom}]}>
                <TextInput
                  ref={inputRef}
                  value={inputText}
                  onChangeText={setInputText}
                  onContentSizeChange={handleContentSizeChange}
                  placeholder='Введите запрос...'
                  placeholderTextColor='#999'
                  multiline
                  scrollEnabled={inputHeight >= MAX_INPUT_HEIGHT}
                  blurOnSubmit={false}
                  editable={(!isGeneralQuestion || isUsefulAnswer) && !isLoading}
                  style={styles.input}
                  textAlignVertical='top'
                  returnKeyType='default'
                  keyboardType='default'
                  autoCapitalize={Platform.OS === 'web' ? undefined : 'sentences'}
                  autoCorrect
                  onKeyPress={(e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
                    const isShift = (e.nativeEvent as any).shiftKey;

                    if (e.nativeEvent.key === 'Enter' && (isShift || Platform.OS !== 'web')) {
                      return;
                    }

                    if (e.nativeEvent.key === 'Enter') {
                      e.preventDefault();
                      sendMessage(inputText);
                    }
                  }}
                />
                <TouchableOpacity
                  style={styles.sendButton}
                  onPress={() => sendMessage(inputText)}
                  disabled={(isGeneralQuestion && !isUsefulAnswer) || isLoading}>
                  <images.SendMessage width={24} height={24} color='#00a859' />
                </TouchableOpacity>
              </View>
            )}
          </View>
        </GradientView>
      </GradientView>
    </View>
  );
}
